import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Space, Card, Typography, DatePicker, Button, message, Tag, Grid } from 'antd';
import { SearchOutlined, WhatsAppOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { stockNotificationsApi, whatsappApi } from '../../utils/api';
import WhatsAppProviderBadge from '../whatsapp/WhatsAppProviderBadge';
import ColumnsCardList from '../common/ColumnsCardList';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/fr_FR';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const ROWS_PER_PAGE = 50;

const NotificationsTable = () => {
  // Mobile (< md) : cartes à la place du tableau, filtres en pleine largeur ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: ROWS_PER_PAGE,
    total: 0,
  });
  const [sendingMessage, setSendingMessage] = useState(null);

  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      
      const filters = {
        page,
        pageSize: ROWS_PER_PAGE,
        status: statusFilter,
        search: searchTerm
      };

      const response = await stockNotificationsApi.getAll(filters);
      
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setPagination({
          current: response.data.data.pagination.page,
          pageSize: response.data.data.pagination.pageSize,
          total: response.data.data.pagination.totalCount
        });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      message.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
  }, []);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchNotifications(1);
  };

  const handleTableChange = (pagination) => {
    fetchNotifications(pagination.current);
  };

  const handleSendWhatsApp = async (record) => {
    console.log('Sending message to:', record);
    try {
      setSendingMessage(record.id);
      
      // 1. Envoyer le message WhatsApp
      const response = await whatsappApi.sendAvailability({
        phoneNumber: record.phoneNumber,
        articleName: record.fullName || record.productName,
        notificationId: record.id,
        articleUrl: record.productImage // ou lien vers produit si disponible
      });

      if (response.data.success) {
        message.success('Message WhatsApp envoyé avec succès');
        
        // 2. Marquer comme envoyé dans la base
        await stockNotificationsApi.markAsSent(record.id);
        
        // 3. Rafraîchir la liste
        fetchNotifications(pagination.current);
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      message.error('Erreur lors de l\'envoi du message WhatsApp');
    } finally {
      setSendingMessage(null);
    }
  };

  const handleCancelNotification = async (id) => {
    try {
      await stockNotificationsApi.cancel(id);
      message.success('Notification annulée');
      fetchNotifications(pagination.current);
    } catch (error) {
      console.error('Error cancelling notification:', error);
      message.error('Erreur lors de l\'annulation');
    }
  };

  const columns = [
    {
      title: 'Date demande',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Client',
      key: 'client',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.customerName}</Text>
          <Text type="secondary">{record.phoneNumber}</Text>
        </Space>
      ),
    },
    {
      title: 'Produit',
      key: 'product',
      render: (_, record) => (
        <Space>
          {record.productImage && (
            <img src={record.productImage} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
          )}
          <Space direction="vertical" size={0}>
            <Text>{record.productName}</Text>
            {record.variantName && (
              <Tag color="blue" className="private-tag-accent" >{record.variantName}</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Stock Actuel',
      dataIndex: 'currentStock',
      key: 'currentStock',
      render: (stock) => (
        <Tag color={stock > 0 ? 'success' : 'error'}>
          {stock} en stock
        </Tag>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          notified: 'green',
          cancelled: 'default'
        };
        const labels = {
          pending: 'En attente',
          notified: 'Notifié',
          cancelled: 'Annulé'
        };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                loading={sendingMessage === record.id}
                onClick={() => handleSendWhatsApp(record)}
                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                disabled={record.currentStock <= 0}
              >
                Notifier
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => handleCancelNotification(record.id)}
              />
            </>
          )}
          {record.status === 'notified' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Envoyé le {dayjs(record.notifiedAt).format('DD/MM/YYYY')}
            </Text>
          )}
        </Space>
      ),
    }
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="center" wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap style={isMobile ? { width: '100%' } : undefined}>
            <Search
              placeholder="Rechercher client..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              style={{ width: isMobile ? '100%' : 300 }}
            />
            <Select
              placeholder="Statut"
              allowClear
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setPagination(prev => ({ ...prev, current: 1 }));
                // Le useEffect se déclenchera si on ajoute statusFilter aux dépendances,
                // sinon on appelle fetch directement
                // Ici fetchNotifications utilise l'état courant, donc attention aux closures.
                // Mieux vaut passer le filtre directement ou utiliser un useEffect.
              }}
              style={{ width: isMobile ? '100%' : 150 }}
            >
              <Option value="pending">En attente</Option>
              <Option value="notified">Notifié</Option>
              <Option value="cancelled">Annulé</Option>
            </Select>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Filtrer
            </Button>
          </Space>
          <Text strong>
            {pagination.total} notification{pagination.total > 1 ? 's' : ''}
          </Text>
        </Space>
        <WhatsAppProviderBadge />

        {isMobile ? (
          /* Mobile : une carte par notification (client en haut, produit, stock, statut, actions en bas) */
          <ColumnsCardList
            columns={columns}
            dataSource={notifications}
            loading={loading}
            headerKeys={['client']}
            actionKeys={['actions']}
            emptyText="Aucune notification trouvée"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page) => fetchNotifications(page)
            }}
          />
        ) : (
        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          locale={{
            emptyText: 'Aucune notification trouvée',
          }}
        />
        )}
      </Space>
    </Card>
  );
};

export default NotificationsTable;
